import { useState, useEffect, useCallback, useRef } from 'react';
import { getMessageTracking, getTrackingBatch } from '@/api/tracking';
import type { MessageTracking, TrackingSummary } from '@/types/email';

interface TrackingCache {
  summary: TrackingSummary;
  fullData?: MessageTracking;
  fetchedAt: number;
}

const trackingCache = new Map<string, TrackingCache>();
const CACHE_TTL = 30000; // 30 seconds

/**
 * Hook to fetch tracking summary for a single message
 */
export function useMessageTracking(messageId: string | null) {
  const [tracking, setTracking] = useState<MessageTracking | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // Identity anchor (X4): the detail header keeps this hook mounted across
  // message changes. Without it, an out-of-order response for message A pins
  // A's open/recipient tracking under message B indefinitely.
  const currentMessageIdRef = useRef<string | null>(messageId);

  const fetchTracking = useCallback(async () => {
    if (!messageId) {
      return;
    }
    const requestId = messageId;

    // Check cache first
    const cached = trackingCache.get(messageId);
    if (cached?.fullData && Date.now() - cached.fetchedAt < CACHE_TTL) {
      if (currentMessageIdRef.current === requestId) {
        setTracking(cached.fullData);
      }
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await getMessageTracking(messageId);

      // Update cache regardless (the data is valid for `requestId`).
      trackingCache.set(messageId, {
        summary: data.summary,
        fullData: data,
        fetchedAt: Date.now(),
      });

      // Drop the response if the caller navigated to another message (X4).
      if (currentMessageIdRef.current !== requestId) {
        return;
      }
      setTracking(data);
    } catch (err) {
      if (currentMessageIdRef.current !== requestId) {
        return;
      }
      console.error('Failed to fetch message tracking:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch tracking'));
    } finally {
      if (currentMessageIdRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [messageId]);

  useEffect(() => {
    // Reset per message (X4) so message B never briefly shows A's tracking, and
    // update the identity anchor so an in-flight A response can't land under B.
    currentMessageIdRef.current = messageId;
    setTracking(null);
    setError(null);
    fetchTracking();
  }, [messageId, fetchTracking]);

  return { tracking, isLoading, error, refetch: fetchTracking };
}

/**
 * Hook to fetch tracking summaries for multiple messages (batch)
 * Returns a map of messageId -> summary
 */
export function useBatchTracking(messageIds: string[]) {
  const [summaries, setSummaries] = useState<Record<string, TrackingSummary>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (messageIds.length === 0) {
      setSummaries({});
      return;
    }

    // Check which IDs need fetching (not in cache or expired)
    const now = Date.now();
    const cachedSummaries: Record<string, TrackingSummary> = {};
    const idsToFetch: string[] = [];

    for (const id of messageIds) {
      const cached = trackingCache.get(id);
      if (cached && now - cached.fetchedAt < CACHE_TTL) {
        cachedSummaries[id] = cached.summary;
      } else {
        idsToFetch.push(id);
      }
    }

    // If all are cached, use cache
    if (idsToFetch.length === 0) {
      setSummaries(cachedSummaries);
      return;
    }

    // Fetch missing ones
    setIsLoading(true);
    setError(null);

    getTrackingBatch(idsToFetch)
      .then((batchResult) => {
        // Convert batch result to TrackingSummary format
        const newSummaries: Record<string, TrackingSummary> = {};
        for (const [id, data] of Object.entries(batchResult)) {
          const summary: TrackingSummary = {
            totalOpens: data.totalOpens,
            realOpens: data.realOpens,
            firstOpenedAt: null, // Not included in batch endpoint
            lastOpenedAt: data.lastOpenedAt,
            recipients: [], // Not included in batch endpoint
          };
          newSummaries[id] = summary;

          // Update cache
          trackingCache.set(id, {
            summary,
            fetchedAt: Date.now(),
          });
        }

        // Combine cached and newly fetched
        setSummaries({ ...cachedSummaries, ...newSummaries });
      })
      .catch((err) => {
        console.error('Failed to fetch batch tracking:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch tracking'));
        // On error, still use cached data if available
        setSummaries(cachedSummaries);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [messageIds.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  return { summaries, isLoading, error };
}

/**
 * Get tracking summary for a single message from cache
 * (for use when you already have batch data loaded)
 */
export function getTrackingSummaryFromCache(messageId: string): TrackingSummary | null {
  const cached = trackingCache.get(messageId);
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL) {
    return cached.summary;
  }
  return null;
}

/**
 * Invalidate cache for a message (e.g., after sending a new email)
 */
export function invalidateTrackingCache(messageId: string) {
  trackingCache.delete(messageId);
}
